import { Button, ButtonHandle } from "@progress/kendo-react-buttons";
import { Icon } from "@progress/kendo-react-common";
import { Dialog } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { Grid, GridCellProps, GridColumn, GridItemChangeEvent } from "@progress/kendo-react-grid";
import { Label } from "@progress/kendo-react-labels";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Popover, Tooltip } from "@progress/kendo-react-tooltip";
import { useEffect, useRef, useState } from "react";
import { NumberEditGridCell } from ".";
import marginsApi from "../../../api/margins";
import { useApi } from "../../../hooks/useApi";
import DateUtility from "../../../utilities/dateUtilities";
import { CustomGridCell } from "../../grid";
import { LoadingIndicator } from "../../LoadingIndicator";
import { Separator } from "../../utilities";
import { BetaValue, BetaValueType } from "./BetaValue";

interface IBetaValuesEditorProps {
  marginId: number;
  disabled: boolean;
  userRoles: any;
  unit?: string;
  onDataUpdate?: (data: BetaValue[]) => void;
}

export const BetaValuesEditor = ({
  marginId,
  disabled,
  userRoles,
  unit,
  onDataUpdate,
}: IBetaValuesEditorProps) => {
  const { isRiskTaker, isBroker } = userRoles;
  const dialogTitle = isBroker ? "Add Broker Commission" : "Add Fee";
  const selectorLabel = isBroker ? "Commission Type" : "Fee Type";

  const getBetaCubesApi = useApi(marginsApi.getBetaCubes);
  const [loading, setLoading] = useState(false);
  const [displayField, setDisplayField] = useState(BetaValueType.TwoWay);
  const [betas, setBetas] = useState<Record<number, any>>({});
  const [updatedData, setUpdatedData] = useState<BetaValue[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [gridData, setGridData] = useState<any[]>([]);
  const [editId, setEditId] = useState<number>();
  const [focusTarget, setFocusTarget] = useState<{ betaId: number; contractMonth: string }>();

  const [betasUsed, setBetasUsed] = useState<number[]>([]);
  const [betasToAdd, setBetasToAdd] = useState<any[]>([]);
  const [contractMonths, setContractMonths] = useState<string[]>([]);
  const [selectedBeta, setSelectedBeta] = useState<any>();

  const _export: any = useRef(null);
  const _grid: any = useRef(null);
  const singleFeeColumnHeader = " Constant";

  useEffect(() => {
    const dates: Date[] = [];
    const currentDate = new Date();
    currentDate.setDate(1); // First of the month

    // Generate 36 months of dates
    for (let i = 0; i <= 36; i++) {
      dates.push(new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1));
    }

    setContractMonths(dates.map((d) => DateUtility.formatDateYMD(d)));
  }, []);

  useEffect(() => {
    setEditId(0);
    getBetaCubesApi.request(marginId);
  }, [marginId]);

  useEffect(() => {
    setLoading(getBetaCubesApi.loading);
    setUpdatedData(getBetaCubesApi.data?.betaCubes ?? []);

    const sortedBetas = (getBetaCubesApi.data?.betas ?? []).sort((a: any, b: any) =>
      a.hasTermStructure === b.hasTermStructure
        ? a.name.localeCompare(b.name)
        : a.hasTermStructure === false
          ? 1
          : -1,
    );

    setBetas(
      sortedBetas.reduce(
        (acc: any, { betaId, ...others }: any, index: number) => {
          acc[betaId] = { ...others, rank: index + 1 };
          return acc;
        },
        {} as Record<
          number,
          { name: string; hasTermStructure: boolean; isStandAlone: boolean; units: string }
        >,
      ),
    );
  }, [getBetaCubesApi.loading, getBetaCubesApi.data]);

  useEffect(() => {
    if (onDataUpdate) onDataUpdate(updatedData);
  }, [updatedData]);

  useEffect(() => {
    if (updatedData) {
      const groupedValues: Record<string, any> = {};
      const colData: Record<string, any> = {};
      const betaIdList = new Set<number>();

      updatedData
        .filter((betaValue) => betaValue.isActive)
        .forEach((betaValue) => {
          const { betaId, betaCubeId, contractMonth } = betaValue;
          const key = contractMonth ?? singleFeeColumnHeader;
          const { name, shortName, hasTermStructure, isStandAlone, rank, units } = betas[betaId];
          colData[shortName] = { betaId, name, shortName, rank, units };
          betaIdList.add(betaId);

          if (!groupedValues[key]) groupedValues[key] = { contractMonth: key };
          groupedValues[key][shortName] = {
            betaId,
            betaCubeId,
            isEditable:
              (hasTermStructure && key !== singleFeeColumnHeader) ||
              (!hasTermStructure && key === singleFeeColumnHeader),
            isEditing: betaId === editId,
            value: betaValue[displayField],
          };
        });

      setBetasUsed([...betaIdList]);
      setColumns(Object.values(colData).sort((a, b) => a.rank - b.rank));
      setGridData(
        Object.values(groupedValues).sort((a, b) => a.contractMonth.localeCompare(b.contractMonth)),
      );
    }
  }, [updatedData, displayField, editId]);

  const betaTypeList = [
    { text: "Bid", value: BetaValueType.Bid },
    { text: "Ask", value: BetaValueType.Ask },
    { text: "Two-Way", value: BetaValueType.TwoWay },
  ];

  const handleItemChange = ({ dataItem, field, value, syntheticEvent }: GridItemChangeEvent) => {
    if (syntheticEvent?.type === "keydown" && typeof value === "string") {
      // This is a hack because the grid's onKeyDown event isn't useful
      processKeyDown(dataItem, field, value?.toLowerCase() || "");
      return;
    }
    if (!field) return;

    const numValue = typeof value === "number" ? value : parseFloat(value) || null;
    const validMessage = valueValidator(numValue);

    const itemCopy = { ...dataItem };
    itemCopy[field].value = numValue;
    if (validMessage) itemCopy[field + "Valid"] = validMessage;
    else delete itemCopy[field + "Valid"];

    let dataCopy = [...gridData];
    const index = dataCopy.findIndex((d) => d.contractMonth === dataItem.contractMonth);
    if (index >= 0) {
      dataCopy[index] = itemCopy;
      setGridData(dataCopy);
    }

    const searchContract =
      dataItem.contractMonth === singleFeeColumnHeader ? null : dataItem.contractMonth;

    if (!validMessage) {
      const newData: BetaValue[] = updatedData.map((item: BetaValue) => {
        return item.contractMonth === searchContract && item.betaId === dataItem[field].betaId
          ? { ...item, [displayField]: numValue, isModified: true }
          : item;
      });

      setUpdatedData(newData);
    }
  };

  const processKeyDown = (dataItem: any, field: string | undefined, keyPress: string) => {
    const obj = dataItem[field || ""];
    const { betaId, value } = obj;
    const index = columns.findIndex((c) => c.betaId === betaId);
    if (!betaId || index < 0) return;

    if (keyPress === "alt+enter") {
      const validMessage = valueValidator(value);
      if (validMessage.length > 0) return;

      const newData: BetaValue[] = updatedData.map((item: BetaValue) =>
        item.betaId === betaId && item.contractMonth && item.contractMonth >= dataItem.contractMonth
          ? { ...item, [displayField]: value, isModified: true }
          : item,
      );
      setUpdatedData(newData);
    } else if (keyPress === "enter") {
      const newIndex = index < columns.length - 1 ? index + 1 : 0;
      setEditId(columns[newIndex].betaId);
    } else if (keyPress === "shift+enter") {
      const newIndex = index > 0 ? index - 1 : columns.length - 1;
      setEditId(columns[newIndex].betaId);
    }
  };

  const valueValidator = (value: number | null) =>
    value != null && value < 0 ? "Must be non-negative" : "";

  const getFirstEditableContractMonth = (shortName: string) =>
    gridData.find((row) => row[shortName])?.contractMonth;

  const activateColumnEditing = (betaId: number, contractMonth: string) => {
    if (disabled) return;
    setEditId(betaId);
    setFocusTarget({ betaId, contractMonth });
  };

  const onClickAddFee = (e: any) => {
    e.preventDefault();

    const betaList = Object.keys(betas)
      .map((str) => parseInt(str))
      .filter((k) => !betasUsed.includes(k))
      .map((k) => ({ id: k, value: betas[k] }))
      .sort((a, b) => a.value.name.localeCompare(b.value.name));

    setBetasToAdd(betaList);
  };

  const onDeleteBeta = (id: number) => {
    const dataCopy = updatedData.map((d) => {
      if (d.betaId === id) {
        d.isActive = false;
        d.isModified = true;
      }
      return d;
    });

    setUpdatedData(dataCopy);
    setBetasToAdd([]);
    setSelectedBeta("");
  };

  const addNewBeta = () => {
    const dataCopy = [...updatedData];

    if (dataCopy.some((d) => d.betaId === selectedBeta.id)) {
      dataCopy.forEach((d) => {
        if (d.betaId === selectedBeta.id) {
          d.isActive = true;
          d.isModified = true;
        }
      });
    } else {
      if (selectedBeta.value.hasTermStructure) {
        contractMonths.forEach((m) =>
          dataCopy.push({
            betaCubeId: 0,
            betaId: selectedBeta.id,
            contractMonth: m,
            bidValue: null,
            askValue: null,
            twoWayValue: 0,
            isModified: true,
            isActive: true,
          }),
        );
      } else {
        dataCopy.push({
          betaCubeId: 0,
          betaId: selectedBeta.id,
          contractMonth: null,
          bidValue: null,
          askValue: null,
          twoWayValue: 0,
          isModified: true,
          isActive: true,
        });
      }
    }

    setUpdatedData(dataCopy);
    setBetasToAdd([]);
    setSelectedBeta("");
  };

  const disableAdd = () => {
    return (
      betasUsed.length === Object.keys(betas).length ||
      betasUsed.map((k) => betas[k]).some((b) => b.isStandAlone)
    );
  };

  const excelExport = () => {
    if (_export.current && _grid.current)
      _export.current.save(gridData.slice(0, -3), _grid.current.columns);
  };

  const CustomRowHeaderCell = (props: GridCellProps) => {
    const field = props.field || "";
    const value = props.dataItem[field];
    const format = props.format || "g";

    const dateValue = DateUtility.format(value, format);

    return (
      <CustomGridCell {...props}>
        <span style={{ fontWeight: "bolder" }}>{dateValue.length ? dateValue : value}</span>
      </CustomGridCell>
    );
  };

  const AddFeeButton = () => {
    return (
      <Button
        fillMode="outline"
        icon="plus"
        size="small"
        themeColor="primary"
        title={dialogTitle}
        onClick={onClickAddFee}
        disabled={disableAdd()}
      />
    );
  };

  const HelpButton = () => {
    const anchor = useRef<ButtonHandle>(null);
    const [show, setShow] = useState(false);

    const handleClick = () => {
      setShow(!show);
    };

    return (
      <>
        <Button
          ref={anchor}
          fillMode="outline"
          icon="info-circle"
          size="small"
          themeColor="primary"
          title="Help"
          onClick={handleClick}
          disabled={show}
        />

        <Popover
          show={show}
          anchor={anchor.current && anchor.current.element}
          className="info-popover"
          position="right"
        >
          <EditorHelp />

          <Separator />
          <div style={{ textAlign: "center" }}>
            <Button size="small" onClick={handleClick}>
              Close
            </Button>
          </div>
        </Popover>
      </>
    );
  };

  const TopLeftHeaderCell = () => {
    return (
      <div style={{ display: "flex", margin: "1rem" }}>
        <HelpButton />
        <AddFeeButton />
      </div>
    );
  };

  const EditorHelp = () => {
    return (
      <>
        <div style={{ marginBottom: "0.5rem" }}>Click on a column header to edit the values</div>

        {isRiskTaker && (
          <GridLayout>
            <GridLayoutItem col={1} row={1}>
              Tab
            </GridLayoutItem>
            <GridLayoutItem col={2} row={1}>
              Edit the next value down the column
            </GridLayoutItem>
            <GridLayoutItem>Shift+Tab</GridLayoutItem>
            <GridLayoutItem>Edit the previous value in the column</GridLayoutItem>
            <GridLayoutItem>Enter</GridLayoutItem>
            <GridLayoutItem>Edit the next column</GridLayoutItem>
            <GridLayoutItem>Shift+Enter</GridLayoutItem>
            <GridLayoutItem>Edit the previous column</GridLayoutItem>
            <GridLayoutItem>Alt+Enter</GridLayoutItem>
            <GridLayoutItem>
              Repeat the current value down the remainder of the column
            </GridLayoutItem>
          </GridLayout>
        )}

        {isBroker && (
          <GridLayout gap={{ rows: 0, cols: 5 }}>
            <GridLayoutItem col={1} row={2}>
              <Label>Adder Fee</Label>
            </GridLayoutItem>
            <GridLayoutItem col={2} row={2}>
              Standard fee for derivative brokers
            </GridLayoutItem>
            <GridLayoutItem col={2} row={3}>
              e.g., $0.01 / gallon
            </GridLayoutItem>
            <GridLayoutItem col={1} row={4}>
              <Label>Multipler Fee</Label>
            </GridLayoutItem>
            <GridLayoutItem col={2} row={4}>
              Standard fee for insurance brokers
            </GridLayoutItem>
            <GridLayoutItem col={2} row={5}>
              e.g., 0.05 for 5%``
            </GridLayoutItem>
          </GridLayout>
        )}
      </>
    );
  };

  const DeletePopover = ({ title, id, onConfirm }: any) => {
    const anchor = useRef<any | null>(null);
    const [show, setShow] = useState(false);

    const handleClick = () => {
      setShow(!show);
    };

    return (
      <>
        &nbsp;
        <Icon
          ref={anchor}
          name="trash"
          onClick={handleClick}
          className="info-icon"
          style={{ color: "#999" }}
        />
        <Popover
          show={show}
          anchor={anchor.current && anchor.current.element}
          position="bottom"
          className="info-popover"
        >
          <div>Delete {title}?</div>

          <Button size={"small"} onClick={(_) => setShow(false)}>
            Cancel
          </Button>
          <Button themeColor={"primary"} size={"small"} onClick={(_) => onConfirm(id)}>
            Delete
          </Button>
        </Popover>
      </>
    );
  };

  const CustomColumnHeaderCell = (props: any) => {
    const { title, units, isEditing, onClick } = props;
    const unitDisplay = unit?.length > 0 ? units.replace("unit", unit) : units;

    return (
      <th style={{ color: isEditing ? "green" : "inherit", textAlign: "left" }}>
        <span style={{ textWrap: "wrap", textDecoration: "underline" }}>
          <a href="#" title="Click to Edit" onClick={onClick}>
            <span style={{ fontWeight: "bold" }}>{title}</span>
          </a>
        </span>

        <DeletePopover {...props} onConfirm={onDeleteBeta} />

        <div>{unitDisplay ? ` (${unitDisplay})` : ""}</div>
      </th>
    );
  };

  return (
    <div>
      <LoadingIndicator loading={loading} />

      {!loading && (
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <ExcelExport ref={_export} />

          {/* HIDING THIS CHIP LIST FOR NOW */}
          {/* <div style={{ display: "flex", margin: "0.5rem", justifyContent: "center" }}>
              <ChipList
                data={betaTypeList}
                selection={"single"}
                value={displayField}
                disabled={disabled}
                onChange={(e) => setDisplayField(e.value)}
              />
          </div> */}

          {columns.length && gridData.length ? (
            <Grid ref={_grid} data={gridData} resizable={true} onItemChange={handleItemChange}>
              <GridColumn
                field={"contractMonth"}
                width={110}
                title={" "}
                format={"MMM yyyy"}
                cells={{ data: CustomRowHeaderCell, headerCell: TopLeftHeaderCell }}
              />

              {columns.map((c, i) => (
                <GridColumn
                  key={i + 1}
                  field={c.shortName}
                  width={120}
                  title={c.name}
                  editable={!disabled}
                  cells={{
                    data: (props: GridCellProps) => (
                      <NumberEditGridCell
                        {...props}
                        onActivate={() =>
                          activateColumnEditing(c.betaId, props.dataItem.contractMonth)
                        }
                        autoFocus={
                          focusTarget?.betaId === c.betaId &&
                          focusTarget?.contractMonth === props.dataItem.contractMonth
                        }
                      />
                    ),
                    headerCell: (props) => (
                      <CustomColumnHeaderCell
                        title={props.title}
                        id={c.betaId}
                        units={c.units}
                        isEditing={editId === c.betaId}
                        onClick={() =>
                          activateColumnEditing(c.betaId, getFirstEditableContractMonth(c.shortName))
                        }
                      />
                    ),
                  }}
                />
              ))}
            </Grid>
          ) : (
            <AddFeeButton />
          )}
        </Tooltip>
      )}

      {betasToAdd.length > 0 && (
        <Dialog title={dialogTitle} onClose={() => setBetasToAdd([])}>
          <Label style={{ paddingRight: 5 }}>{selectorLabel}</Label>
          <DropDownList
            data={betasToAdd}
            textField="value.name"
            dataItemKey="id"
            style={{ width: 350 }}
            onChange={(e) => setSelectedBeta(e.target.value)}
          />

          <div
            style={{
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
              gap: 10,
              marginTop: 5,
            }}
          >
            <Button size="small" onClick={() => setBetasToAdd([])}>
              Cancel
            </Button>
            <Button size="small" themeColor="primary" disabled={!selectedBeta} onClick={addNewBeta}>
              Add
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
};
